import React, { useEffect, useState } from "react";
import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
//import { examSetupApi, setupError } from "../examSetupApi";
import axios from "axios";
const outlinedLabelProps = {
  shrink: true,
  sx: {
    backgroundColor: "#fff",
    color: "#344767",
    fontSize: "0.95rem",
    fontWeight: 500,
    px: 0.75,
    "&.Mui-focused": { color: "#1976d2" },
  },
};

const empty = {
  acyear_id: "",
  component_type: "",
  rate: "",
  duration: "",
  min_scripts: "0",
  min_amount: "0",
};

function examSetupApi() {
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  if (!user?.client_id || !user?.college_id)
    throw new Error("Select a college before managing exam setup");
  const base = `${process.env.REACT_APP_API_BASE_URL}/api/exam-setup/${user.client_id}/${user.college_id}`;
  const token = sessionStorage.getItem("token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  return {
    get: async (path, params) => (await axios.get(`${base}${path}`, { params, headers })).data.data,
    post: async (path, data) => (await axios.post(`${base}${path}`, data, { headers })).data.data,
    put: async (path, data) => (await axios.put(`${base}${path}`, data, { headers })).data.data,
    delete: async (path) => (await axios.delete(`${base}${path}`, { headers })).data,
  };
}

function setupError(error) {
  return error.response?.data?.message || error.message || "Unable to save exam setup";
}
export default function ValuationRates() {
  const [years, setYears] = useState([]),
    [rows, setRows] = useState([]);
  const [form, setForm] = useState(empty),
    [editing, setEditing] = useState(null);
  const [message, setMessage] = useState(null),
    [busy, setBusy] = useState(false);
  const api = examSetupApi();
  const load = async () => {
    try {
      const [yearRows, rateRows] = await Promise.all([
        api.get("/academic-years"),
        api.get("/valuation-rates"),
      ]);
      setYears(yearRows);
      setRows(rateRows);
    } catch (error) {
      setMessage({ type: "error", text: setupError(error) });
    }
  };
  useEffect(() => {
    load();
  }, []);
  const field = (name, value) => setForm((current) => ({ ...current, [name]: value }));
  const save = async () => {
    setBusy(true);
    try {
      if (editing) await api.put(`/valuation-rates/${editing}`, form);
      else await api.post("/valuation-rates", form);
      setMessage({ type: "success", text: "Valuation rate saved" });
      setEditing(null);
      setForm(empty);
      await load();
    } catch (error) {
      setMessage({ type: "error", text: setupError(error) });
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this valuation rate?")) return;
    try {
      await api.delete(`/valuation-rates/${id}`);
      await load();
    } catch (error) {
      setMessage({ type: "error", text: setupError(error) });
    }
  };
  const yearName = (id) => years.find((year) => year.acyear_id === id)?.academic_year || id;
  const edit = (row) => {
    setEditing(row.exam_valuation_rate_id);
    setForm({
      acyear_id: row.acyear_id,
      component_type: row.component_type,
      rate: row.rate,
      duration: row.duration ?? "",
      min_scripts: row.min_scripts ?? 0,
      min_amount: row.min_amount ?? 0,
    });
  };
  const columns = [
    {
      field: "serialNo",
      headerName: "S. No.",
      width: 80,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      valueGetter: (params) => {
        // params.api.getRowIndex(params.id) returns the index in the current page
        return params.api ? params.api.getRowIndex(params.id) + 1 : "";
      },
    },
    {
      field: "academic_year",
      headerName: "Academic Year",
      minWidth: 150,
      flex: 0.9,
      valueGetter: (params) => yearName(params.row.acyear_id),
    },
    { field: "component_type", headerName: "Component", minWidth: 150, flex: 1 },
    {
      field: "rate",
      headerName: "Rate",
      minWidth: 100,
      flex: 0.6,
      valueFormatter: (params) => `₹${params.value}`,
    },
    { field: "duration", headerName: "Duration", minWidth: 100, flex: 0.6 },
    { field: "min_scripts", headerName: "Min Scripts", minWidth: 110, flex: 0.7 },
    {
      field: "min_amount",
      headerName: "Min Amount",
      minWidth: 120,
      flex: 0.7,
      valueFormatter: (params) => `₹${params.value}`,
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 180,
      flex: 0.9,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => edit(params.row)}>
            Edit
          </Button>
          <Button
            size="small"
            color="error"
            onClick={() => remove(params.row.exam_valuation_rate_id)}
          >
            Delete
          </Button>
        </Stack>
      ),
    },
  ];
  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>
        EXAM VALUATION RATES
      </Typography>
      {message && (
        <Alert severity={message.type} onClose={() => setMessage(null)} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1} flexWrap="wrap" useFlexGap>
          <TextField
            select
            label="Academic Year"
            size="small"
            required
            InputLabelProps={outlinedLabelProps}
            value={form.acyear_id}
            onChange={(e) => field("acyear_id", e.target.value)}
            SelectProps={{ native: true }}
            //InputLabelProps={{ shrink: true }}
          >
            <option value="">Select Academic Year</option>
            {years.map((x) => (
              <option key={x.acyear_id} value={x.acyear_id}>
                {x.academic_year}
              </option>
            ))}
          </TextField>
          <TextField
            label="Component Type"
            InputLabelProps={outlinedLabelProps}
            size="small"
            required
            value={form.component_type}
            onChange={(e) => field("component_type", e.target.value)}
          />
          <TextField
            label="Rate ₹"
            InputLabelProps={outlinedLabelProps}
            size="small"
            type="number"
            required
            value={form.rate}
            onChange={(e) => field("rate", e.target.value)}
          />
          <TextField
            label="Duration"
            InputLabelProps={outlinedLabelProps}
            size="small"
            type="number"
            value={form.duration}
            onChange={(e) => field("duration", e.target.value)}
          />
          <TextField
            label="Min Scripts"
            InputLabelProps={outlinedLabelProps}
            size="small"
            type="number"
            value={form.min_scripts}
            onChange={(e) => field("min_scripts", e.target.value)}
          />
          <TextField
            label="Min Amount ₹"
            InputLabelProps={outlinedLabelProps}
            size="small"
            type="number"
            value={form.min_amount}
            onChange={(e) => field("min_amount", e.target.value)}
          />
          <Button
            variant="contained"
            disabled={busy || !form.acyear_id || !form.component_type.trim() || form.rate === ""}
            onClick={save}
            sx={{
              color: "#fff !important",
              "&:hover": { color: "#fff !important" },
              "&.Mui-disabled": { color: "rgba(0, 0, 0, 0.26) !important" },
            }}
          >
            {editing ? "Update Valuation Rate" : "Add Valuation Rate"}
          </Button>
          {editing && (
            <Button
              onClick={() => {
                setEditing(null);
                setForm(empty);
              }}
            >
              Cancel
            </Button>
          )}
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ height: 400, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.exam_valuation_rate_id}
            pageSize={10}
            rowsPerPageOptions={[5, 10, 25]}
            disableSelectionOnClick
          />
        </Box>
      </Paper>
    </Box>
  );
}
